import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CardPurseIcon,
  CardsItemIcon,
  FiservCardInterface,
  FiservCardPurse,
  Hint,
  Inscription,
  List,
  Toggle,
} from '@common-fe/common-fe';

import { MASKED_CARD_NUMBER } from '@/common/constants';

import useCardAccessModalStore from './stores/useCardAccessModal.store';

const CARD_PURSE_ICON_SIZE = '32px';
const LOCKED_BY_HEALTH_PLAN_HINT = 'Account is locked based on plan rules';

interface Props {
  purse?: FiservCardPurse;
  card?: FiservCardInterface;
  withoutToggle?: boolean;
}

export const CardAccessListItem: React.FC<Props> = ({ purse, card, withoutToggle }) => {
  const {
    state: { lockedPursesIds, isCardLocked },
    handleToggleLockedPurse,
    handleToggleCardLock,
  } = useCardAccessModalStore();
  const isLockedByHealthPlan = useMemo(
    () => purse?.isLockedByHealthPlan === true,
    [purse?.isLockedByHealthPlan]
  );

  const isLockedMode = useMemo(() => {
    if (card) {
      return isCardLocked;
    }
    const isPurseLocked = purse?.id && lockedPursesIds.includes(purse?.id);
    return isPurseLocked;
  }, [purse, card, lockedPursesIds, isCardLocked]);

  const [isActive, setIsActive] = useState<boolean>(!isLockedMode);

  const handleToggleChange = (value: boolean) => {
    if (isLockedByHealthPlan) return;
    setIsActive(value);
    if (card) {
      handleToggleCardLock(!value);
    } else if (purse?.id) {
      handleToggleLockedPurse(purse.id, !value);
    }
  };

  useEffect(() => {
    if (card) {
      setIsActive(!isCardLocked);
    } else if (purse?.id) {
      const isPurseLocked = lockedPursesIds.includes(purse?.id);
      setIsActive(!isPurseLocked);
    }
  }, [lockedPursesIds, purse?.id, isCardLocked, card]);

  const toggleContent = (
    <Box direction="row" align="center" gap="xs">
      <Toggle value={isActive} twoColorsMode onChange={handleToggleChange} disabled={isLockedByHealthPlan} />
      <Inscription weight={500} color="textSecondary" size="small">
        {isActive ? 'Unlocked' : 'Locked'}
      </Inscription>
    </Box>
  );

  return (
    <List.Item
      direction="row"
      justify="between"
      height="66px"
      data-testid="card-access-table-element_id"
      border={{ color: isLockedMode && !isLockedByHealthPlan ? 'dangerBorder' : 'border1', size: 'small' }}
      background={isLockedMode && !isLockedByHealthPlan ? 'dangerContainer' : 'canvas'}
      round="button1Round"
      align="center"
      pad={{ horizontal: 'l', vertical: 'm' }}
      style={{ cursor: 'initial' }}
    >
      <Box width={{ max: withoutToggle ? '550px' : '420px' }} direction="row" align="center">
        {card ? (
          <Box direction="row" align="center" gap="s" width="80%">
            <CardsItemIcon color="iconAccent" size="large" />
            <Inscription weight="bold" color="textBody">
              {!card?.last4Number ? MASKED_CARD_NUMBER : card?.last4Number}
            </Inscription>
          </Box>
        ) : (
          <Box direction="row" align="center" gap="s">
            <CardPurseIcon type={purse?.accountType} size={CARD_PURSE_ICON_SIZE} />
            <Inscription weight="bold" color="textBody" hasEllipsisMode>
              {purse?.accountName}
            </Inscription>
          </Box>
        )}
      </Box>
      {!withoutToggle && (
        <Box direction="row" align="center" width="20%">
          {isLockedByHealthPlan ? (
            <Hint hintAlign="vertical" hintElement={toggleContent}>
              <Inscription size="12px" textAlign="center">
                {LOCKED_BY_HEALTH_PLAN_HINT}
              </Inscription>
            </Hint>
          ) : (
            toggleContent
          )}
        </Box>
      )}
    </List.Item>
  );
};
